import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { Router, ActivatedRoute, NavigationExtras, Params } from '@angular/router';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { EventTypes } from '@site-mate/dashpivot-shared-library';

import { TmpI18NService } from 'app/i18n/tmp-i18n.service';
import { SegmentService } from 'app/segment/segment.service';
import { WINDOW } from 'app/shared/factory/window.factory';
import { BannerKeys } from 'app/shared/model/banner-keys.enum';
import { BannerMetadata } from 'app/shared/model/banner-metadata.model';
import { ModuleViews } from 'app/shared/model/module-views.enum';
import { AppUtilService } from 'app/shared/service/app-util.service';
import { EventsService } from 'app/shared/service/events/events.service';
import { TeamService } from 'app/shared/service/team.service';
import { UserService } from 'app/user/user.service';

@Component({
  selector: 'cc-apps-tool-bar',
  templateUrl: 'apps-tool-bar.component.html',
  styleUrls: ['../../../../styles/components/tool-bar.component.scss', './apps-tool-bar.component.scss'],
})
export class AppsToolBarComponent implements OnInit {
  @Input()
  canAdd = true;

  @Input()
  app;

  @Input()
  selectedForms: Set<string>;

  @Input()
  isRegisterView;

  @Input()
  exportingCSV;

  @Input()
  exportingPDF;

  @Input()
  downloadingPDF;

  @Input()
  registerViewFilterCount;

  @Output() readonly remove = new EventEmitter<void>();
  @Output() readonly resetDateFilter = new EventEmitter<void>();
  @Output() readonly searchDateFilter = new EventEmitter<void>();
  @Output() readonly toggleRegisterView = new EventEmitter<void>();
  @Output() readonly deleteSelected = new EventEmitter<void>();
  @Output() readonly exportPDF = new EventEmitter<void>();
  @Output() readonly downloadPDF = new EventEmitter<void>();
  @Output() readonly exportCsvVisibleColumns = new EventEmitter<void>();
  @Output() readonly exportCsvAllColumns = new EventEmitter<void>();
  @Output() readonly exportRegisterOnly = new EventEmitter<void>();
  @Output() readonly registerViewSetting = new EventEmitter<void>();
  @Output() readonly resetRegisterViewFilter = new EventEmitter<void>();
  @Output() readonly exportPDFWithOriginalPhotos = new EventEmitter<void>();

  readonly bannerKeys = BannerKeys;
  readonly bannerMetadata = BannerMetadata;

  exportRegViewInfo: string;
  downloadFormsInfo: string;
  downloadRegisterOnlyInfo: string;
  downloadCsvVisibleColumnsInfo: string;
  downloadCsvAllColumnsInfo: string;
  orgTemplateInfo: string;
  view: ModuleViews;
  exportPDFWithOriginalPhotosInfo: string;
  unSubscribe = new Subject();
  moduleViews = ModuleViews;

  constructor(
    private readonly appUtilService: AppUtilService,
    private readonly userService: UserService,
    private readonly teamService: TeamService,
    private readonly segmentService: SegmentService,
    private readonly router: Router,
    private readonly i18nService: TmpI18NService,
    private readonly route: ActivatedRoute,
    private readonly reCaptchaV3Service: ReCaptchaV3Service,
    private readonly activatedRoute: ActivatedRoute,
    private readonly eventsService: EventsService,
    @Inject(WINDOW) private readonly window: Window,
  ) {}

  ngOnInit(): void {
    this.exportRegViewInfo = this.i18nService.getMessage('exportRegViewInfo');
    this.downloadFormsInfo = this.i18nService.getMessage('downloadFormsInfo');
    this.downloadRegisterOnlyInfo = this.i18nService.getMessage('downloadRegisterOnlyInfo');
    this.downloadCsvAllColumnsInfo = this.i18nService.getMessage('downloadCsvAllColumnsInfo');
    this.downloadCsvVisibleColumnsInfo = this.i18nService.getMessage('downloadCsvVisibleColumnsInfo');
    this.orgTemplateInfo = this.i18nService.getMessage('orgTemplateInfo');
    this.exportPDFWithOriginalPhotosInfo = this.i18nService.getMessage('exportPDFWithOriginalPhotosInfo');
    this.view = this.activatedRoute.snapshot.data.view;
  }

  onEdit() {
    this.eventsService.trackEvent(EventTypes.EditTemplateButtonClicked, {
      Context: 'Form List',
    });
    this.callRecaptchaV3();

    const context = this.app.isOrganisationTemplate ? 'companies' : 'teams';

    const path = ['/', 'apps', context, this.app.parents[0], 'active', 'grid', this.app.id];

    const options: NavigationExtras = {
      relativeTo: this.route,
      queryParams: {
        fromPage: this.router.url,
      },
    };

    void this.router.navigate(path, options);
  }

  callRecaptchaV3() {
    const editTemplate = 'editTemplate';
    this.reCaptchaV3Service.execute(editTemplate).pipe(takeUntil(this.unSubscribe));
  }

  navigateToCompany(): void {
    const templateId = this.app.uniqueAppId;
    const queryParams: Params = { search: templateId };
    const urlSearchWithTemplateId = this.router.serializeUrl(
      this.router.createUrlTree(['/', 'apps', 'companies', this.currentCompanyId(), 'active', 'grid'], {
        queryParams,
        queryParamsHandling: 'merge',
      }),
    );
    this.window.open(urlSearchWithTemplateId, '_blank');
  }

  backToTeamTemplateList() {
    let { templatesListFilters } = this.route.snapshot.parent.params;
    if (templatesListFilters) {
      try {
        templatesListFilters = JSON.parse(templatesListFilters);
      } catch (e) {
        templatesListFilters = undefined;
      }
    }

    this.eventsService.trackEvent(EventTypes.TemplateBackButtonClicked);
    if (this.app.isOrganisationTemplate) {
      void this.router.navigate(['/', 'apps', 'companies', this.app.parents[0], 'grid'], {
        queryParams: templatesListFilters,
      });
    } else {
      void this.router.navigate(['/', 'apps', 'teams', this.app.parents[0], 'grid'], {
        queryParams: templatesListFilters,
      });
    }
  }

  canEdit() {
    if (this.app.isExampleTemplate) {
      return this.userService.isAdmin();
    }

    return this.userService.isTeamControllerOrAbove();
  }

  get isWorkflow() {
    return this.app.columns?.length > 0;
  }

  isCompanyController(): boolean {
    return this.userService.isCompanyControllerOf(this.currentCompanyId());
  }

  isMobile(): boolean {
    return this.appUtilService.isMobile;
  }

  isTablet(): boolean {
    return this.appUtilService.isTablet;
  }

  private currentCompanyId(): string {
    return this.teamService.getCurrentTeam().companyId;
  }
}
